/**
 * Aufrufe der Zeiterfassung -- F5-01 bis F5-04.
 *
 * Die Taetigkeitsarten kommen aus der Stammdatenverwaltung und nicht von
 * hier: Sie sind ein Stammdatum wie die Prioritaet, und eine zweite
 * Zugriffsstelle liefe auseinander.
 */

import type { components } from '../../api/schema';
import { download, request } from '../../lib/apiClient';

export type TimeEntry = components['schemas']['TimeEntryRead'];
export type TimeReport = components['schemas']['TimeReportRead'];
export type TimeReportBucket = components['schemas']['TimeReportBucket'];

/** Die zugelassenen Achsen -- deckungsgleich mit `GROUP_BY_AXES` im Dienst. */
export const REPORT_AXES = ['customer', 'user', 'activity_type', 'day', 'ticket'] as const;

export type ReportAxis = (typeof REPORT_AXES)[number];

export interface TimeFilters {
  ticket_id?: string;
  user_id?: string;
  customer_id?: string;
  activity_type_id?: string;
  since?: string;
  until?: string;
  billable?: boolean;
}

export function listTimeEntries(filters: TimeFilters): Promise<TimeEntry[]> {
  return request<TimeEntry[]>('/time-entries', { query: { ...filters } });
}

export function createTimeEntry(payload: Record<string, unknown>): Promise<TimeEntry> {
  return request<TimeEntry>('/time-entries', { method: 'POST', body: payload });
}

export function updateTimeEntry(
  entryId: string,
  payload: Record<string, unknown>,
): Promise<TimeEntry> {
  return request<TimeEntry>(`/time-entries/${entryId}`, { method: 'PATCH', body: payload });
}

export function deleteTimeEntry(entryId: string): Promise<undefined> {
  return request<undefined>(`/time-entries/${entryId}`, { method: 'DELETE' });
}

export function fetchTimeReport(filters: TimeFilters, groupBy: ReportAxis): Promise<TimeReport> {
  return request<TimeReport>('/time-entries/report', {
    query: { ...filters, group_by: groupBy },
  });
}

/**
 * Holt die Auswertung als CSV und uebergibt sie dem Browser.
 *
 * Ueber `fetch` und nicht ueber einen Link: Ein `<a href>` traegt den
 * `Authorization`-Kopf nicht mit, und ein Token in der Adresszeile stuende im
 * Verlauf des Browsers. Dieselbe Ueberlegung wie beim Ticketausdruck.
 */
export function downloadTimeReport(
  filters: TimeFilters,
  groupBy: ReportAxis,
): Promise<{ blob: Blob; filename: string | null }> {
  const params = new URLSearchParams({ group_by: groupBy });
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return download(`/time-entries/report.csv?${params.toString()}`);
}
